import { useState, useContext, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@material-ui/core";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, ColumnApi, FilterChangedEvent, GridApi, GridReadyEvent } from "ag-grid-community";
import { dateTimeZoneConverter } from "../../../../../utils/commonFunctions/date";
import initCapitalize from "../../../../../utils/commonFunctions/initCapitalize";
import useRouteParam from "../../../../../hooks/useRouteParam";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import isEmployerRoute from "../../../../../utils/commonFunctions/isEmployerRoute";

import AssignPermissionDialog from "./AssignPermissionDialog";
import { useEmployerRoute } from "../../../../../hooks";
import { AuthContext } from "../../../../../contexts";
import { getPermissionsFromGroupOwner } from "../../../../../utils/getPermissionsFromGroupOwner";
import { CustomFilter, CustomFloatingFilter } from "../../../../shared";
import UserManagementFilter from "../customFilterForUserManagement/UserManagementFilter";

const UserManagementGrid_V2 = ({
	gridData,
	gridFilterSchema,
	setGridFilterSchema,
	onFilterChange,
	loading
}: {
	gridData: any;
	gridFilterSchema: any;
	setGridFilterSchema: any;
	onFilterChange: (filterModel: any) => void;
	loading: boolean;
}) => {
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const queryGroupNumber = useRouteParam("gn");
	const navigate = useNavigate();
	const agGridRef = useRef<any>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const lastFilterModel = useRef<any>(null);
	const filterRestoredRef = useRef(false);
	const isRestoringFilter = useRef(false);

	const { user } = useContext(AuthContext);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
	const [selectedGroupNumber, setSelectedGroupNumber] = useState<number | null>(null);
	const [dynamicPermissionSchema, setDynamicPermissionSchema] = useState<[]>([]);
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [selectedUserName, setSelectedUserName] = useState("");

	const [groupNumberAssignedList, setGroupNumberAssignedList] = useState<any[]>([]);

	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const userManagementPermission =
		user && isEmployerRoute() && queryGroupNumber
			? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.employer_settings_menu.user_management_sub
					.self
			: user
			? getPermissionsFromGroupOwner(user, 0)?.settings_menu.user_management_sub.self
			: null;

	// const onGridReady = (params: GridReadyEvent) => {
	// 	setGridApi(params.api);
	// 	params.api.sizeColumnsToFit();
	// };

	// const onFilterChanged = useCallback(
	// 	(params: FilterChangedEvent) => {
	// 		if (isRestoringFilter.current) {
	// 			console.log("Filter change ignored during restore");
	// 			return;
	// 		}

	// 		const model = params.api.getFilterModel();
	// 		lastFilterModel.current = model;
	// 		filterRestoredRef.current = false;
	// 		console.log("onfilterChanged");
	// 		onFilterChange(model);
	// 	},
	// 	[onFilterChange]
	// );

	const onFilterChanged = useCallback(
		(params: FilterChangedEvent) => {
			if (isRestoringFilter.current) {
				console.log("Filter change ignored during restore");
				return;
			}

			const model = params.api.getFilterModel();

			// ✅ Immediately update the stored filter model
			lastFilterModel.current = model;
			filterRestoredRef.current = false;

			console.log("onFilterChanged triggered");

			// ✅ Debounce ONLY the API call
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}

			debounceTimer.current = setTimeout(() => {
				console.log("Debounced filter change");
				onFilterChange(model);
			}, 300);
		},
		[onFilterChange]
	);

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(api);
		setAgGridColumnAPI(columnApi);
		setGridApi(api);
		// console.log("handleAgGridReady");
		api.sizeColumnsToFit();
	};

	const handleFirstDataRendered = (params: any) => null;

	const handleDateCellRenderer = ({ value }: { value: any }) => {
		const date = dateTimeZoneConverter(value as string) as string;
		return <div>{value ? date : "N/A"}</div>;
	};

	const handleUserRoleCellRenderer = ({ value }: { value: string }) => {
		const role = initCapitalize(value);
		return <div>{role}</div>;
	};

	const handleUserInformaitonClick = useCallback(
		async (_queryGroupNumber: number) => {
			const focusedCell = agGridRef?.current.api.getFocusedCell();
			const row = agGridRef?.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
			const { data } = row;
			const pathname = isEmployerRoute() ? `/employer/settings/user?gn=${_queryGroupNumber}` : "/settings/user";
			const key = "user_id";
			navigate(pathname, {
				state: {
					[key]: data._id
				}
			});
			// console.log("isEm", isEmployerRoute);
		},
		[navigate]
	);

	const handleGroupAssignClick = useCallback(
		(_queryGroupNumber: number) => {
			const focusedCell = agGridRef?.current.api.getFocusedCell();
			const row = agGridRef?.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
			const { data } = row;

			const pathname = isEmployerRoute()
				? `/employer/settings/user/assign-groups?gn=${_queryGroupNumber}`
				: "/settings/user/assign-groups";
			const key = "user_id";
			const roleKey = "role";
			navigate(pathname, {
				state: {
					[key]: data._id,
					[roleKey]: data.role
				}
			});
		},
		[navigate]
	);

	const handleUserInformationCellRenderer = useCallback(
		({ queryGroupNumber }: { queryGroupNumber: number }) => {
			return (
				<Button
					className="ag-grid-cell-button"
					variant="outlined"
					onClick={() => handleUserInformaitonClick(queryGroupNumber)}
				>
					See Details
				</Button>
			);
		},
		[handleUserInformaitonClick]
	);

	const handleGroupAssignCellRender = useCallback(
		({ queryGroupNumber }: { queryGroupNumber: number }) => {
			return (
				<Button
					className="ag-grid-cell-button"
					variant="outlined"
					onClick={() => handleGroupAssignClick(queryGroupNumber)}
				>
					Group Assign
				</Button>
			);
		},
		[handleGroupAssignClick]
	);

	const handleGroupPermissionClick = useCallback(async (_queryGroupNumber: number) => {
		const focusedCell = agGridRef?.current.api.getFocusedCell();
		const row = agGridRef?.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
		const { data } = row;

		const groupNumberAssignedList = data.permissions || [];
		setGroupNumberAssignedList(groupNumberAssignedList);

		const dynamicSchema = data.permissions || [];
		setSelectedUserId(data._id);
		setDynamicPermissionSchema(dynamicSchema);
		setSelectedUserName(`${data.first_name || ""} ${data.last_name || ""}`.trim());
		setSelectedGroupNumber(_queryGroupNumber);
		setDialogOpen(true);
	}, []);

	const handleGroupPermissionCellRender = useCallback(
		({ queryGroupNumber }: { queryGroupNumber: number }) => {
			return (
				<Button
					className="ag-grid-cell-button"
					variant="outlined"
					onClick={() => handleGroupPermissionClick(queryGroupNumber)}
				>
					Assign Permission
				</Button>
			);
		},
		[handleGroupPermissionClick]
	);

	const handleRegisteredCellRender = ({ value }: { value: boolean }) => {
		return <div>{value ? "Yes" : "No"}</div>;
	};

	const handleApprovedByCellRender = ({ value }: { value: any }) => {
		return <div>{typeof value === typeof {} ? value.user_name : value}</div>;
	};

	useEffect(() => {
		if (!gridApi) return;

		if (loading) {
			gridApi.showLoadingOverlay();
		} else {
			gridApi.hideOverlay();

			if (lastFilterModel.current && !filterRestoredRef.current) {
				console.log("Restoring filters in useEffect");
				isRestoringFilter.current = true;

				// ✅ Refresh floating filter inputs
				gridApi.onFilterChanged();
				// ✅ Re-apply the filter model
				gridApi.setFilterModel(lastFilterModel.current);

				// gridApi.refreshHeader();

				// ✅ Reset restoring flag on next tick
				setTimeout(() => {
					isRestoringFilter.current = false;
				}, 0);

				filterRestoredRef.current = true;
			}
		}
	}, [loading, gridApi, gridData]);

	useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, []);

	return (
		<div className="user-management-grid-container glb-tab-grid-container">
			<div className="ag-theme-alpine" style={{ height: "400px", width: "100%" }}>
				<AssignPermissionDialog
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					selected={selectedCheckboxes}
					setSelected={setSelectedCheckboxes}
					permissionsSchema={dynamicPermissionSchema}
					userId={selectedUserId}
					userName={selectedUserName}
					groupNumberAssignedList={groupNumberAssignedList}
				/>

				<AgGridReact
					ref={agGridRef}
					rowData={gridData}
					onGridReady={handleAgGridReady}
					onFirstDataRendered={handleFirstDataRendered}
					onFilterChanged={onFilterChanged}
					animateRows
					rowDragManaged
					frameworkComponents={{
						handleDateCellRenderer: handleDateCellRenderer,
						handleUserInformationCellRenderer: handleUserInformationCellRenderer,
						handleGroupAssignCellRender: handleGroupAssignCellRender,
						handleGroupPermissionCellRender: handleGroupPermissionCellRender,
						handleApprovedByCellRender: handleApprovedByCellRender,
						handleRegisteredCellRender: handleRegisteredCellRender,
						handleUserRoleCellRenderer: handleUserRoleCellRenderer,
						customFloatingFilter: CustomFloatingFilter,
						customFilter: UserManagementFilter,
						onFilterChange,
						schema: gridFilterSchema,
						setGridFilterSchema: setGridFilterSchema
					}}
					defaultColDef={{
						filter: "agTextColumnFilter",
						floatingFilter: true,
						resizable: true,
						floatingFilterComponentParams: { suppressFilterButton: true }
					}}
					defaultColGroupDef={{ marryChildren: true }}
					rowSelection="single"
					onCellValueChanged={(params: CellValueChangedEvent) => {
						params.api.refreshCells({
							force: true,
							columns: ["assign_group", "user_information"]
						});
					}}
					enableCellChangeFlash={true}
				>
					<AgGridColumn
						field="user_name"
						headerName="User Name"
						filter="customFilter"
						floatingFilterComponent="customFloatingFilter"
						floatingFilterComponentParams={{ suppressFilterButton: true, onFilterChange }}
					></AgGridColumn>

					<AgGridColumn
						field="is_registered"
						headerName="Registered User"
						cellRenderer="handleRegisteredCellRender"
					></AgGridColumn>
					<AgGridColumn
						field="email"
						headerName="Email"
						filter="customFilter"
						floatingFilterComponent="customFloatingFilter"
					></AgGridColumn>
					<AgGridColumn
						field="role"
						headerName="Role"
						cellRenderer="handleUserRoleCellRenderer"
						filter="customFilter"
						floatingFilterComponent="customFloatingFilter"
					></AgGridColumn>
					<AgGridColumn
						field="date"
						headerName="Creation Date"
						cellRenderer="handleDateCellRenderer"
					></AgGridColumn>
					<AgGridColumn
						field="last_login_date"
						headerName="Last Login Date"
						cellRenderer="handleDateCellRenderer"
					></AgGridColumn>
					<AgGridColumn
						field="user_information"
						headerName="User Information"
						cellRenderer="handleUserInformationCellRenderer"
						filter={false}
						cellRendererParams={{ queryGroupNumber }}
						suppressSizeToFit={true}
					></AgGridColumn>
					{userManagementPermission && userManagementPermission.assign_group && (
						<AgGridColumn
							field="assign_group"
							headerName="Groups Assignment"
							cellRenderer="handleGroupAssignCellRender"
							filter={false}
							suppressSizeToFit={true}
							cellRendererParams={{ queryGroupNumber }}
						></AgGridColumn>
					)}
					{userManagementPermission && userManagementPermission.assign_permission && (
						<AgGridColumn
							field="assign_group"
							headerName="User Permission"
							cellRenderer="handleGroupPermissionCellRender"
							filter={false}
							suppressSizeToFit={true}
							cellRendererParams={{ queryGroupNumber }}
						/>
					)}
				</AgGridReact>
			</div>
		</div>
	);
};

export default UserManagementGrid_V2;
